<template>
  <LoadingWrapper :state="status">
    <SideBarLayoutContentHero>
      <template #hero>
        <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
          <BaseCard class="flex flex-col p-6">
            <div class="flex flex-row">
              <BackButton backup-route-name="assessments" size="XS" class="mr-2 -ml-3" />
              <div class="flex flex-col">
                <p v-if="data.title" class="text-body-medium text-on-surface-variant truncate">
                  {{ data.feedbackTopic.title }}
                </p>
                <h1 class="text-headline-large leading-tight">
                  {{ data.title ?? data.feedbackTopic.title }}
                </h1>
              </div>
            </div>
            <div class="text-on-surface-variant mt-4 flex flex-col gap-1.5">
              <div v-if="data.finishedAt" class="flex flex-row items-center">
                <BaseIcon size="18" class="mr-1">today</BaseIcon>
                <span class="text-body-medium">
                  {{ $t('common.finishedAt') }}:
                  <span class="capitalize">
                    {{
                      useDisplayDate(data.finishedAt, {
                        dayName: 'short',
                      })
                    }}
                  </span>
                </span>
              </div>
              <div class="flex flex-row items-center">
                <BaseIcon size="18" class="mr-1">today</BaseIcon>
                <span class="text-body-medium">
                  {{ $t('common.createdAt') }}:
                  <span class="capitalize">
                    {{
                      useDisplayDate(data.createdAt, {
                        dayName: 'short',
                      })
                    }}
                  </span>
                </span>
              </div>
            </div>
            <div class="mt-4">
              <BaseButtonGroup size="M">
                <BaseComputedButtonGroup
                  class="flex w-full flex-row justify-end"
                  size="M"
                  menu-button-color="tonal"
                  :buttons="[
                    {
                      id: 'editTitleAndMessage',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('survey.editTitleAndMessage'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'edit',
                      callback: openEditTitleAndMessageDialog,
                    },
                    {
                      id: 'editCoachees',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('coaching.assessment.editCoachees'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'group_add',
                      callback: openEditCoacheesDialog,
                    },
                    {
                      id: 'editPeers',
                      flexibelConfigurations: [{ type: 'menu', order: 2 }],
                      label: $t('coaching.assessment.editPeers'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'group_add',
                      callback: openEditPeersDialog,
                    },
                    {
                      id: 'stop',
                      condition: data.state === SurveyState.running,
                      flexibelConfigurations: [
                        { type: 'text', order: 1 },
                        { type: 'icon', order: 2 },
                        { type: 'menu', order: 3 },
                      ],
                      label: $t('common.finish'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'stop',
                      callback: openFinishAssessmentDialog,
                    },
                    {
                      id: 'reopen',
                      condition: data.state === SurveyState.finished,
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('common.reopen'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'restart_alt',
                      callback: reopenAssessment,
                    },
                    {
                      id: 'list',
                      flexibelConfigurations: [{ type: 'menu' }],
                      label: $t('survey.showQuestions'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'list',
                      callback: openShowQuestionsDialog,
                    },
                    {
                      id: 'delete',
                      flexibelConfigurations: [{ type: 'menu', order: 1 }],
                      label: $t('common.delete'),
                      color: 'tonal',
                      shape: 'square',
                      icon: 'delete',
                      destructive: true,
                      callback: openDeleteAssessmentDialog,
                    },
                  ]"
                />
              </BaseButtonGroup>
            </div>
          </BaseCard>
          <BaseCard class="grid grid-cols-2 gap-4 p-6">
            <div class="flex w-full flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant mr-2 shrink-0">people</BaseIcon>
              <div class="flex flex-col">
                <BaseMemberAvatars
                  class="bg-surface-container"
                  :members="data.coachees.map((c) => c.member)"
                />
                <span class="text-body-medium text-on-surface-variant"
                  >{{ data.coachees.length }}
                  {{ $t('common.coachees', data.coachees.length) }}</span
                >
              </div>
            </div>
            <div class="flex w-full flex-row items-center gap-2 overflow-hidden">
              <div v-if="data.includeSelfFeedback" class="flex min-w-0 flex-col">
                <BaseCircularProgressIndictator
                  :percentage="percentageSelfFeedback"
                  :radius="24"
                  :thickness="12"
                />
                <span
                  class="text-body-medium text-on-surface-variant min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                  >{{ totalSelfDone }}/{{ totalNumberOfCoachees }} Selbsteinschätzungen</span
                >
              </div>
            </div>
            <div v-if="data.peers.length > 0" class="flex w-full flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant mr-2 shrink-0">people</BaseIcon>
              <div class="flex flex-col">
                <BaseMemberAvatars class="bg-surface-container" :members="data.peers" />
                <span class="text-body-medium text-on-surface-variant"
                  >{{ data.peers.length }} {{ $t('common.peers', data.peers.length) }}</span
                >
              </div>
            </div>
            <div v-if="data.peers.length > 0" class="flex flex-row items-center gap-2">
              <div class="flex min-w-0 flex-col">
                <BaseCircularProgressIndictator
                  :percentage="percentageTotalPeerFeedback"
                  :radius="24"
                  :thickness="12"
                />
                <span
                  class="text-body-medium text-on-surface-variant min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                  >{{ totalNumberOfFinishedPeerFeedbacks }}/{{
                    totalNumberOfRequiredPeerFeedbacks
                  }}
                  Einschätzungen</span
                >
              </div>
            </div>
          </BaseCard>
        </div>
      </template>

      <div class="flex flex-col gap-4">
        <ul class="flex flex-col gap-0.5">
          <li
            v-for="coachee in coacheesWithPeers"
            :key="coachee.member.id"
            class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large"
          >
            <div class="flex flex-row gap-4 p-3">
              <BaseMemberAvatar :member="coachee.member" />
              <div class="flex grow flex-col">
                <h3 class="text-title-medium">
                  {{ memberFullName(coachee.member) }}
                </h3>
                <span class="text-body-medium text-on-surface-variant -mt-0.5">
                  {{ coachee.member.email }}
                </span>
              </div>
              <div
                v-if="data.includeSelfFeedback"
                class="border-outline-variant ml-auto flex flex-row items-center"
                :class="[data.peers.length > 0 && 'border-r pr-4']"
              >
                <div
                  class="flex flex-row rounded-full p-1.5 px-3"
                  :class="[
                    coachee.selfFeedbackDone && 'bg-primary-container',
                    !coachee.selfFeedbackDone && 'bg-surface-container-highest',
                  ]"
                >
                  <div
                    v-if="coachee.selfFeedbackDone"
                    class="relative mr-1.5 -ml-1 flex h-4 w-4 grow-0 items-center justify-center"
                  >
                    <BaseIcon size="14" :filled="true" class="text-on-primary z-20">check</BaseIcon>
                    <BaseShape
                      shape="soft-burst"
                      class="text-primary absolute top-1/2 left-1/2 z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 stroke-2"
                    />
                  </div>
                  <span class="text-label-medium" v-if="coachee.selfFeedbackDone"
                    >Selbsteinschätzung erledigt</span
                  >
                  <span class="text-label-medium" v-else>Selbsteinschätzung offen</span>
                </div>
              </div>
              <div v-if="data.peers.length > 0" class="flex flex-row items-center gap-2">
                <div class="flex flex-col">
                  <span class="text-label-medium"
                    >{{ numberOfFinishedPeersForCoachee(coachee.member.id) }}/{{
                      coachee.peers.length
                    }}
                  </span>
                  <span class="text-label-medium"
                    >{{ $t('common.peers', coachee.peers.length) }}
                  </span>
                </div>
                <div class="flex flex-row -space-x-2">
                  <div
                    v-for="peer in coachee.peers"
                    class="bg-surface-container relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full p-0.5"
                  >
                    <BaseMemberAvatar
                      :member="peer"
                      size="32"
                      :class="[!peer.isFinished && 'opacity-70']"
                    />
                    <div
                      v-if="peer.isFinished"
                      class="absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center"
                    >
                      <BaseIcon size="14" :filled="true" class="text-on-primary z-20"
                        >check</BaseIcon
                      >
                      <BaseShape
                        shape="soft-burst"
                        class="text-primary absolute right-0 z-10 h-full w-full stroke-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </SideBarLayoutContentHero>
    <BaseDialog
      type="basic"
      :is-open="isFinishAssessmentDialogOpen"
      :title="$t('coaching.assessment.finish.title')"
      :description="$t('coaching.assessment.finish.description')"
      @close="closeFinishAssessmentDialog"
    >
      <template #dismiss>
        <BaseTextButton size="M" color="text" @click="closeFinishAssessmentDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton size="M" color="filled" @click="clickFinishAssessment">{{
          $t('coaching.assessment.finish.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
    <BaseDialog
      type="basic"
      :is-open="isDeleteAssessmentDialogOpen"
      :title="$t('survey.delete.confirmation')"
      :description="$t('survey.delete.description')"
      @close="closeDeleteAssessmentDialog"
    >
      <template #dismiss>
        <BaseTextButton size="M" color="text" @click="closeDeleteAssessmentDialog">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton size="M" color="text" @click="clickDeleteAssessment">{{
          $t('survey.delete.action')
        }}</BaseTextButton>
      </template>
    </BaseDialog>
    <EditAssessmentTitleAndMessageDialog
      :is-open="isEditTitleAndMessageDialogOpen"
      :assessment="data"
      @close-dialog="closeEditTitleAndMessageDialog"
      @assessment-updated="reload"
    />
    <EditAssessmentCoacheesDialog
      :is-open="isEditCoacheesDialogOpen"
      :assessment="data"
      @close-dialog="closeEditCoacheesDialog"
      @assessment-updated="reload"
    />
    <EditAssessmentPeersDialog
      :is-open="isEditPeersDialogOpen"
      :assessment="data"
      @close-dialog="closeEditPeersDialog"
      @assessment-updated="reload"
    />
    <AllQuestionsDialog
      v-if="isShowQuestionsDialogOpen"
      :is-open="isShowQuestionsDialogOpen"
      :feedback-topics="[data.feedbackTopic]"
      @close="closeShowQuestionsDialog"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import BackButton from '@/core/components/BackButton.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useAssessment } from '../components/use-assessment'
  import SideBarLayoutContentHero from '@/core/components/layouts/SideBarLayoutContentHero.vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseComputedButtonGroup from '@/core/components/base/button/BaseComputedButtonGroup.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import { SurveyState } from '@/modules/survey/models/survey-state'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseCircularProgressIndictator from '@/core/components/base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import { computed } from 'vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import { memberFullName } from '@/core/models/member/member-name'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import { useDisplayDate } from '@/core/composables/use-display-date'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import AllQuestionsDialog from '@/modules/survey/components/AllQuestionsDialog.vue'
  import EditAssessmentTitleAndMessageDialog from '../components/EditAssessmentTitleAndMessageDialog.vue'
  import EditAssessmentCoacheesDialog from '../components/EditAssessmentCoacheesDialog.vue'
  import EditAssessmentPeersDialog from '../components/EditAssessmentPeersDialog.vue'

  const { data, status, stopAssessment, deleteAssessment, reopenAssessment, reload } =
    useAssessment()
  const {
    isOpen: isEditTitleAndMessageDialogOpen,
    openDialog: openEditTitleAndMessageDialog,
    closeDialog: closeEditTitleAndMessageDialog,
  } = useDialog()
  const {
    isOpen: isEditCoacheesDialogOpen,
    openDialog: openEditCoacheesDialog,
    closeDialog: closeEditCoacheesDialog,
  } = useDialog()
  const {
    isOpen: isEditPeersDialogOpen,
    openDialog: openEditPeersDialog,
    closeDialog: closeEditPeersDialog,
  } = useDialog()
  const {
    isOpen: isDeleteAssessmentDialogOpen,
    openDialog: openDeleteAssessmentDialog,
    closeDialog: closeDeleteAssessmentDialog,
  } = useDialog()
  const {
    isOpen: isFinishAssessmentDialogOpen,
    openDialog: openFinishAssessmentDialog,
    closeDialog: closeFinishAssessmentDialog,
  } = useDialog()
  const {
    isOpen: isShowQuestionsDialogOpen,
    openDialog: openShowQuestionsDialog,
    closeDialog: closeShowQuestionsDialog,
  } = useDialog()

  const totalSelfDone = computed(() => {
    return data.value?.coachees.reduce(
      (acc, coachee) => acc + (coachee.selfFeedbackDone ? 1 : 0),
      0,
    )
  })

  const totalNumberOfFinishedPeerFeedbacks = computed(() => {
    return data.value?.coachees.reduce(
      (acc, coachee) => acc + (coachee.finishedPeerIds.length ?? 0),
      0,
    )
  })

  const totalNumberOfCoachees = computed(() => {
    return data.value?.coachees.length ?? 0
  })

  const totalNumberOfRequiredPeerFeedbacks = computed(() => {
    return (data.value?.peers.length ?? 0) * totalNumberOfCoachees.value
  })

  const percentageSelfFeedback = computed(() => {
    return (totalSelfDone.value / totalNumberOfCoachees.value) * 100
  })

  const percentageTotalPeerFeedback = computed(() => {
    return (
      (totalNumberOfFinishedPeerFeedbacks.value / totalNumberOfRequiredPeerFeedbacks.value) * 100
    )
  })

  const clickFinishAssessment = async () => {
    closeFinishAssessmentDialog()
    await stopAssessment()
  }

  const clickDeleteAssessment = async () => {
    closeDeleteAssessmentDialog()
    await deleteAssessment()
  }

  const coacheesWithPeers = computed(() => {
    return data.value?.coachees.map((coachee) => {
      return {
        ...coachee,
        peers: data.value?.peers.map((peer) => ({
          ...peer,
          isFinished: coachee.finishedPeerIds.includes(peer.id),
        })),
      }
    })
  })

  const numberOfFinishedPeersForCoachee = (coacheeId: string) => {
    return data.value?.coachees.find((c) => c.member.id === coacheeId)?.finishedPeerIds.length ?? 0
  }
</script>
